import { useCallback, useEffect, useState } from 'react';
import { container } from '@web/services/container';
import { getLogger } from '@packages/core/logging';
import { type CvtDiagnosticEntity, FetchDiagnosticDetails, VehicleGraphData } from '@packages/domain/cvt';

import { exportPlotlyPdfWithCharts } from '../components/plotly-pdf-export';
import { prepareCvtPdfReport } from '../components/cvt-pdf/prepare-cvt-pdf-report';

const logger = getLogger('cvt');

const [$fetchDiagnosticDetails] = container.resolveOperations(FetchDiagnosticDetails);

export function useCvtDiagnosticReport(diagnosticId?: string) {
    const [data, setData] = useState<CvtDiagnosticEntity>();
    const [graphData, setGraphData] = useState<VehicleGraphData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

    const fetchDiagnosticDetails = useCallback(async (diagnosticId: string) => {
        setIsLoading(true);
        try {
            const results = await $fetchDiagnosticDetails({ diagnosticId });

            if (results?.data) {
                setData(results.data);
                setGraphData(results.graphData ?? []);
            }
        } catch (error) {
            logger.error(`Failed to fetch the CVT diagnostic report details [${diagnosticId}]`, {
                error,
                diagnosticId,
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    const downloadReport = useCallback(
        async (diagnosticId: string): Promise<boolean> => {
            setDownloadingIds((previous) => {
                const next = new Set(previous);
                next.add(diagnosticId);
                return next;
            });
            await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
            try {
                const details =
                    data?.diagnosticId === diagnosticId
                        ? { data, graphData }
                        : await $fetchDiagnosticDetails({ diagnosticId });

                if (!details?.data) {
                    return false;
                }

                const preparedData = prepareCvtPdfReport(details.data, details.graphData ?? []);
                await exportPlotlyPdfWithCharts(preparedData);
                return true;
            } catch (error) {
                logger.error(`Failed to download the CVT diagnostic report [${diagnosticId}]`, {
                    error,
                    diagnosticId,
                });
                return false;
            } finally {
                setDownloadingIds((previous) => {
                    const next = new Set(previous);
                    next.delete(diagnosticId);
                    return next;
                });
            }
        },
        [data, graphData]
    );

    useEffect(() => {
        if (diagnosticId) {
            fetchDiagnosticDetails(diagnosticId);
        }
    }, [diagnosticId, fetchDiagnosticDetails]);

    return {
        data,
        fetchDiagnosticDetails,
        graphData,
        isLoading,
        downloadingIds,
        downloadReport,
    };
}
